/** @jsx jsx */
import { useEffect } from 'react';
import { jsx } from '@emotion/core';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import coverImg from 'assets/login-cover-img.png';
import {
  AppLogo,
  LoginForm,
  ResetPassword,
  SignUp,
  CreateNewPassword,
  Legals,
} from 'components';
import { appConfigSelector, appOptionsSelector } from 'reducers/app.reducer';
import { appConfig, appOptions } from 'actions/app.actions';
import { ROUTES } from '../../constants';
import {
  Brand,
  Content,
  ContentWrapper,
  Wrapper,
  Illustration,
  cssIllustration,
  cssLogo,
} from './Auth.style';
import { SsoFailure } from 'components/SsoFailure';

const Auth = (): JSX.Element => {
  const { programUrl, postUserRegister: isSubscriptionEnabled } =
    useSelector(appConfigSelector);
  const { list } = useSelector(appOptionsSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!programUrl) {
      // the app store is reinitialised when user logs out but the auth page needs options
      // and config (which are first called when the whole app is mounted, not on auth page)
      // so we need to get those data again when the auth page is displayed after user logged out
      dispatch(appConfig.request());
      dispatch(appOptions.request());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const WEBFILES_BASE = `${programUrl}/web_files/ui/img`;
  const backgroundImgUri = list.find(
    (item) => item.name === '$backgroundLogin',
  )?.value;

  return (
    <Wrapper>
      {/* in dev mode, the API returns '0' by default, */}
      {/* so check backgroundImgUri is a way to prevent from displaying nothing */}
      <Illustration>
        <img
          css={cssIllustration}
          src={
            programUrl && backgroundImgUri && backgroundImgUri !== '0'
              ? `${WEBFILES_BASE}/${backgroundImgUri}`
              : coverImg
          }
          alt="illustration"
        />
      </Illustration>
      <Content>
        <ContentWrapper>
          <Brand>
            <AppLogo login css={cssLogo} />
          </Brand>
          <Switch>
            <Route exact path={ROUTES.LOGIN}>
              <LoginForm />
            </Route>
            {isSubscriptionEnabled && (
              <Route exact path={ROUTES.SUBSCRIBE}>
                <SignUp />
              </Route>
            )}
            <Route exact path={[ROUTES.FORGOT_PASSWORD, ROUTES.RESET_PASSWORD]}>
              <ResetPassword />
            </Route>
            <Route exact path={ROUTES.CREATE_NEW_PASSWORD}>
              <CreateNewPassword />
            </Route>
            <Route exact path={ROUTES.SSO_FAILURE}>
              <SsoFailure />
            </Route>
            <Route>
              <Redirect to={ROUTES.LOGIN} />
            </Route>
          </Switch>
        </ContentWrapper>
        <Legals textProps={{ align: 'center', style: { fontSize: 14 } }} />
      </Content>
    </Wrapper>
  );
};
export default Auth;
